import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Search, ChevronDown, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Natural sort so codes order in sequence: HOE001 < HOE002 < HOE018 < HOE018.1 < HOE018.11
const naturalCompare = (a, b) => {
  const ax = String(a).match(/(\d+|\D+)/g) || [];
  const bx = String(b).match(/(\d+|\D+)/g) || [];
  for (let i = 0; i < Math.max(ax.length, bx.length); i++) {
    const an = ax[i], bn = bx[i];
    if (an === undefined) return -1;
    if (bn === undefined) return 1;
    const aNum = /^\d+$/.test(an), bNum = /^\d+$/.test(bn);
    if (aNum && bNum) {
      const d = parseInt(an, 10) - parseInt(bn, 10);
      if (d !== 0) return d;
    } else if (an !== bn) {
      return an < bn ? -1 : 1;
    }
  }
  return 0;
};

export default function Payments() {
  const { t } = useLang();
  const [loans, setLoans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState({ loanCode: "", date: new Date().toISOString().slice(0, 10), incomeAmount: "", principalAmount: "" });
  const [saving, setSaving] = useState(false);
  const [loanQuery, setLoanQuery] = useState("");
  const [loanOpen, setLoanOpen] = useState(false);
  const loanBoxRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (loanBoxRef.current && !loanBoxRef.current.contains(e.target)) setLoanOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const load = useCallback(() => {
    api.get("/loans").then((r) => setLoans(r.data));
    api.get("/payments").then((r) => setPayments(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const loanByCode = useMemo(
    () => Object.fromEntries(loans.map((l) => [l.code, l])),
    [loans]
  );

  const total = (parseFloat(form.incomeAmount) || 0) + (parseFloat(form.principalAmount) || 0);
  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.loanCode) { toast.error(t("pay.err_select")); return; }
    setSaving(true);
    try {
      const res = await api.post("/payments", {
        loanCode: form.loanCode,
        date: form.date,
        incomeAmount: parseFloat(form.incomeAmount) || 0,
        principalAmount: parseFloat(form.principalAmount) || 0,
      });
      toast.success(`${t("pay.added")} ${fmtRM(res.data.loan.outstanding)}`);
      setForm({ ...form, incomeAmount: "", principalAmount: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const activeLoans = loans
    .filter((l) => l.status !== "Settled")
    .sort((a, b) => naturalCompare(a.code, b.code));
  const selected = loans.find((l) => l.code === form.loanCode);
  const q = loanQuery.trim().toLowerCase();
  const filteredLoans = q
    ? activeLoans.filter((l) => `${l.code} ${l.customerName}`.toLowerCase().includes(q))
    : activeLoans;

  const pickLoan = (l) => {
    setForm({ ...form, loanCode: l.code });
    setLoanQuery("");
    setLoanOpen(false);
  };

  return (
    <Layout>
      <PageHeader title={t("pay.title")} subtitle={t("pay.subtitle")} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 h-fit">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">{t("pay.add")}</h3>
          <form onSubmit={submit} className="space-y-4" data-testid="payments-form">
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">{t("pay.loan")}</label>
              <div className="relative mt-1.5" ref={loanBoxRef}>
                <div
                  data-testid="payments-loan-trigger"
                  onClick={() => setLoanOpen((o) => !o)}
                  className={`${inputCls} flex items-center justify-between cursor-pointer ${loanOpen ? "ring-2 ring-slate-900" : ""}`}
                >
                  <span className={selected ? "text-slate-900" : "text-slate-400"}>
                    {selected ? `${selected.code} — ${selected.customerName}` : t("pay.select_loan")}
                  </span>
                  <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                </div>
                {loanOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg" data-testid="payments-loan-dropdown">
                    <div className="p-2 border-b border-slate-100">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          autoFocus
                          data-testid="payments-loan-search"
                          value={loanQuery}
                          onChange={(e) => setLoanQuery(e.target.value)}
                          placeholder={t("pay.search_loan")}
                          className="w-full pl-8 pr-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-slate-900 outline-none"
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto py-1">
                      {filteredLoans.map((l) => (
                        <button
                          type="button"
                          key={l.code}
                          data-testid={`payments-loan-option-${l.code}`}
                          onClick={() => pickLoan(l)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                        >
                          <span><span className="font-mono-fig font-medium text-slate-900">{l.code}</span> <span className="text-slate-500">— {l.customerName}</span></span>
                          {l.code === form.loanCode && <Check className="h-4 w-4 text-green-600 shrink-0" />}
                        </button>
                      ))}
                      {filteredLoans.length === 0 && <div className="px-3 py-4 text-center text-sm text-slate-400">{t("pay.no_match")}</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {selected && (
              <div className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-600">
                {t("pay.outstanding")}: <span className="font-mono-fig font-semibold text-slate-900">{fmtRM(selected.outstanding)}</span>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">{t("pay.date")}</label>
              <input type="date" data-testid="payments-date-input" className={`${inputCls} mt-1.5`} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500">{t("pay.income")}</label>
                <input type="number" step="0.01" data-testid="payments-income-input" className={`${inputCls} mt-1.5`} value={form.incomeAmount} onChange={(e) => setForm({ ...form, incomeAmount: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500">{t("pay.principal")}</label>
                <input type="number" step="0.01" data-testid="payments-principal-input" className={`${inputCls} mt-1.5`} value={form.principalAmount} onChange={(e) => setForm({ ...form, principalAmount: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <span className="text-xs uppercase text-slate-500 font-semibold">{t("pay.total")}</span>
              <span data-testid="payments-total" className="font-mono-fig font-bold text-slate-900">{fmtRM(total)}</span>
            </div>
            <button data-testid="payments-submit-btn" type="submit" disabled={saving} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("pay.record")}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900">{t("pay.all")}</h3>
          </div>
          <table className="w-full text-sm" data-testid="payments-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5 font-medium">{t("pay.col_date")}</th>
                <th className="px-3 py-2.5 font-medium">{t("pay.col_customer")}</th>
                <th className="px-3 py-2.5 font-medium">{t("pay.col_code")}</th>
                <th className="px-3 py-2.5 font-medium text-right">{t("pay.income")}</th>
                <th className="px-3 py-2.5 font-medium text-right">{t("pay.principal")}</th>
                <th className="px-5 py-2.5 font-medium text-right">{t("pay.col_total")}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => {
                const loan = loanByCode[p.loanCode];
                return (
                  <tr key={p.paymentId} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3 text-slate-600">{p.date}</td>
                    <td className="px-3 py-3">
                      <Link to={`/loans/${p.loanCode}`} className="text-slate-900 font-medium hover:underline">{loan?.customerName || "—"}</Link>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{loan?.customerCode || "—"}</td>
                    <td className="px-3 py-3 text-right font-mono-fig text-green-600">{fmtRM(p.incomeAmount)}</td>
                    <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(p.principalAmount)}</td>
                    <td className="px-5 py-3 text-right font-mono-fig font-semibold text-slate-900">{fmtRM(p.totalPayment)}</td>
                  </tr>
                );
              })}
              {payments.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400">{t("pay.none")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
